import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Plus } from '../../../assets';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { UserContext } from '../../../components/shared/useUser';
import { AuditType } from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';

const AuditTypesPage: React.FC = () => {
  const { t } = useTranslation('complianceAudits');
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const canCreate = hasPermissionTo(['AuditTypesCreate']);
  const canEdit = hasPermissionTo(['AuditTypesEdit']);
  const canDelete = hasPermissionTo(['AuditTypesDelete']);
  const hasAnyAction = canEdit || canDelete;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [auditTypes, setAuditTypes] = useState<AuditType[]>([]);
  const [page, setPage] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<AuditType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingType, setDeletingType] = useState<AuditType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });
  
  const rowsPerPage = 10;

  const maxPages = () => {
    return Math.ceil(auditTypes.length / rowsPerPage);
  };

  const paginatedTypes = auditTypes.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    loadAuditTypes();
  }, [page, selectedStore?.storeNumber]);

  const loadAuditTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await complianceService.auditTypes.getAll(true, selectedStore?.storeNumber);
      setAuditTypes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type?: AuditType) => {
    if (type) {+
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description,
      isActive: type.isActive,
    });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        description: '',
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingType(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      // Check for duplicate name
      const duplicateExists = auditTypes.some(
        (at) => at.name.toLowerCase().trim() === formData.name.toLowerCase().trim()
          && at.id !== editingType?.id,
      );
      if (duplicateExists) {
        setError(t('auditTypes.duplicateName'));
        return;
      }

      if (editingType) {
        await complianceService.auditTypes.update(editingType.id, formData, selectedStore?.storeNumber);
        setSuccess(t('auditTypes.updateSuccess'));
      } else {
        await complianceService.auditTypes.create(formData, selectedStore?.storeNumber);
        setSuccess(t('auditTypes.createSuccess'));
      }
      handleCloseDialog();
      await loadAuditTypes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auditTypes.saveFailed'));
    }
  };

  const handleDeleteClick = (type: AuditType, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeletingType(type);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingType) return;
    try {
      setError(null);
      setSuccess(null);
      await complianceService.auditTypes.delete(deletingType.id, selectedStore?.storeNumber);
      setSuccess(t('auditTypes.deleteSuccess'));
      setDeleteDialogOpen(false);
      setDeletingType(null);
      await loadAuditTypes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auditTypes.deleteFailed'));
      setDeleteDialogOpen(false);
      setDeletingType(null);
    }
  };

  const handleRowClick = (type: AuditType) => {
    handleOpenDialog(type);
  };

  if (loading) {
    return (
      <PageArea>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <PageTitle subTitle={t('auditTypes.pageSubTitle')}>{t('auditTypes.pageTitle')}</PageTitle>
        {canCreate && (
          <Button
            variant="primary"
            size="lg"
            startIcon={<SvgIcon><Plus /></SvgIcon>}
            onClick={() => handleOpenDialog()}
          >
            {t('auditTypes.newAuditType')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Section>
        {auditTypes.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('auditTypes.noTypes')}
            searchTerm={''}
            onClear={() => {}}
            onAdd={() => handleOpenDialog()}
            onAddIsCreating={false}
            addItemText={t('auditTypes.newAuditType')}
            onAddIsDisabled={!canCreate}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {t('auditTypes.pageTitle')}
                </Typography>
                <Pill text={t('auditTypes.typeCount', { count: auditTypes.length })} />
              </Box>
            </Box>

            <TableContainer>
              <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', minWidth: '800px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('common.name')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('common.description')}</TableCell>
                    <TableCell
                      align="center"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t('common.status')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: 'nowrap', width: '150px' }}
                    >
                      {hasAnyAction && t('common.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTypes.map((type) => (
                    <TableRow
                      key={type.id}
                      onClick={() => canEdit && handleRowClick(type)}
                      sx={{
                        cursor: canEdit ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: canEdit ? 'action.hover' : undefined,
                        },
                      }}
                    >
                      <TableCell>
                        <Tooltip title={type.name}>
                          <span
                            style={{
                              display: 'block',
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {type.name}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={type.description || ''}>
                          <span
                            style={{
                              display: 'block',
                              maxWidth: '400px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {type.description}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={type.isActive ? t('common.active') : t('common.inactive')}
                          color={type.isActive ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog(type);
                            }}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(type, e)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingType ? t('auditTypes.editAuditType') : t('auditTypes.newAuditType')}
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            pt={1}
          >
            <TextField
              label={t('common.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={t('common.description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={t('common.active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="primary"
            disabled={!formData.name.trim()}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDeletingType(null);
        }}
        nameOfObject={deletingType?.name || t('auditTypes.auditType')}
      />
    </PageArea>
  );
};

export default AuditTypesPage;
